import React from 'react';
import {
  makeStyles,
  mergeClasses,
  shorthands,
  Text,
  Field,
  RadioGroup,
  Radio,
  Card,
  Badge,
  InfoButton,
} from '@fluentui/react-components';
import {
  Shield24Regular,
  ShieldCheckmark24Regular,
  Warning24Regular,
  ErrorCircle24Regular,
  Info24Regular,
} from '@fluentui/react-icons';
import { gfcPolicyService } from '../../services/gfcPolicyService';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('16px'),
  },
  categoryOption: {
    ...shorthands.margin('4px', '0'),
    transition: 'all 0.2s ease',
    cursor: 'pointer',
    '&:hover': {
      transform: 'translateY(-1px)',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    },
  },
  selectedOption: {
    transform: 'translateY(-1px)',
    boxShadow: '0 4px 12px rgba(16, 110, 190, 0.2)',
  },
  optionContent: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('12px'),
    ...shorthands.padding('16px'),
  },
  optionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  categoryTitle: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('8px'),
  },
  categoryDetails: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('8px'),
  },
  characteristicsList: {
    ...shorthands.margin('0', '0', '0', '16px'),
  },
  complianceInfo: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('4px'),
    ...shorthands.padding('12px'),
    backgroundColor: '#f8f9fa',
    ...shorthands.borderRadius('4px'),
    ...shorthands.border('1px', 'solid', '#e1dfdd'),
  },
  radioGroup: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('4px'),
  },
});

interface RiskCategorySelectorProps {
  value?: 'High' | 'Medium' | 'Low' | 'Trusted';
  onChange: (category: 'High' | 'Medium' | 'Low' | 'Trusted') => void;
  disabled?: boolean;
  complianceRegion?: 'EU' | 'US' | 'APAC' | 'Global';
}

export const RiskCategorySelector: React.FC<RiskCategorySelectorProps> = ({
  value,
  onChange,
  disabled = false,
  complianceRegion,
}) => {
  const styles = useStyles();
  const riskColors = gfcPolicyService.getRiskCategoryColors();
  const complianceRequirements = gfcPolicyService.getComplianceRegionRequirements();

  const riskCategories = [
    {
      key: 'Trusted' as const,
      icon: ShieldCheckmark24Regular,
      title: 'Trusted Partners',
      description: 'Verified financial institutions and long-term strategic partners with established compliance frameworks',
      characteristics: [
        'Mutual compliance agreements in place',
        'Regular security audits and certifications',
        'Established data sharing agreements',
        'Known and verified domain ownership'
      ],
      useCase: 'Major banking partners, regulatory bodies, trusted service providers',
      auditRequirement: 'Annual compliance review',
      monitoringLevel: 'Standard monitoring with quarterly reviews'
    },
    {
      key: 'Low' as const,
      icon: Shield24Regular,
      title: 'Low Risk Domains',
      description: 'Well-known organizations with good security posture but limited business relationship',
      characteristics: [
        'Publicly traded companies with known reputation',
        'Government and educational institutions',
        'Industry associations and standards bodies',
        'Technology vendors with security certifications'
      ],
      useCase: 'Conference organizers, industry groups, certified vendors',
      auditRequirement: 'Semi-annual review',
      monitoringLevel: 'Standard monitoring with automated alerts'
    },
    {
      key: 'Medium' as const,
      icon: Warning24Regular,
      title: 'Medium Risk Domains',
      description: 'Organizations requiring additional scrutiny and monitoring due to limited verification or compliance uncertainty',
      characteristics: [
        'New business relationships under evaluation',
        'Organizations in high-risk jurisdictions',
        'Smaller institutions with limited compliance history',
        'Partners undergoing regulatory changes'
      ],
      useCase: 'New clients, regional banks, emerging market partners',
      auditRequirement: 'Quarterly compliance review',
      monitoringLevel: 'Enhanced monitoring with real-time alerts'
    },
    {
      key: 'High' as const,
      icon: ErrorCircle24Regular,
      title: 'High Risk Domains',
      description: 'Domains requiring maximum scrutiny and restricted access due to significant risk factors',
      characteristics: [
        'Organizations under regulatory investigation',
        'Domains with known security incidents',
        'High-risk geographical jurisdictions',
        'Temporary or emergency access requirements'
      ],
      useCase: 'Crisis communication, regulatory investigations, temporary access',
      auditRequirement: 'Monthly review with executive approval',
      monitoringLevel: 'Continuous monitoring with immediate escalation'
    }
  ];

  const getCategoryIcon = (IconComponent: React.ComponentType<any>, category: string) => {
    const colors = riskColors[category];
    return <IconComponent style={{ color: colors.text }} />;
  };

  const getCategoryBadge = (category: string) => {
    const colors = riskColors[category];
    return (
      <Badge
        appearance="filled"
        style={{
          backgroundColor: colors.background,
          border: `1px solid ${colors.border}`,
          color: colors.text,
        }}
      >
        {category.toUpperCase()} RISK
      </Badge>
    );
  };

  const getComplianceInfo = (category: string) => {
    if (!complianceRegion || !complianceRequirements[complianceRegion]) {
      return null;
    }

    const requirements = complianceRequirements[complianceRegion];
    const categoryData = riskCategories.find(c => c.key === category);

    return (
      <div className={styles.complianceInfo} data-testid="gfc-risk-category-compliance-info">
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }} data-testid="gfc-risk-category-compliance-header">
          <Info24Regular style={{ fontSize: '16px', color: '#106ebe' }} />
          <Text weight="semibold" size={300}>
            {requirements.name} Compliance Requirements
          </Text>
        </div>
        <Text size={200}>
          <strong data-testid="gfc-risk-category-audit-label">Audit Frequency:</strong> {categoryData?.auditRequirement}
        </Text>
        <Text size={200}>
          <strong data-testid="gfc-risk-category-monitoring-label">Monitoring:</strong> {categoryData?.monitoringLevel}
        </Text>
        {category === 'High' && (
          <Text size={200} style={{ color: '#d13438' }}>
            <strong data-testid="gfc-risk-category-additional-requirements-label">⚠️ Additional Requirements:</strong> Executive approval required for all High Risk domain additions
          </Text>
        )}
      </div>
    );
  };

  return (
    <Field
      label={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }} data-testid="gfc-risk-category-label-row">
          <Text weight="semibold">Risk Category</Text>
          <InfoButton
            info={
              <div style={{ maxWidth: '400px' }} data-testid="gfc-risk-category-info-content">
                <Text size={200}>
                  <strong data-testid="gfc-risk-category-info-title">Risk-Based Domain Classification</strong><br data-testid="gfc-risk-category-info-break" />
                  Categorize domains based on your organization's risk assessment. This affects 
                  monitoring levels, audit requirements, and approval workflows for the domains 
                  in this policy.
                </Text>
              </div>
            }
          />
        </div>
      }
    >
      <div className={styles.container} data-testid="gfc-risk-category-selector">
        <Text size={300} style={{ color: '#605e5c', marginBottom: '8px' }}>
          Select the appropriate risk level for domains in this policy:
        </Text>

        <RadioGroup
          value={value}
          onChange={(_, data) => onChange(data.value as 'High' | 'Medium' | 'Low' | 'Trusted')}
          disabled={disabled}
          className={styles.radioGroup}
          data-testid="gfc-risk-category-options"
        >
          {riskCategories.map((category, index) => {
            const isSelected = value === category.key;
            const colors = riskColors[category.key];
            
            return (
              <div key={category.key} data-testid={`gfc-risk-category-option-container-${index}`}>
                <Radio
                  value={category.key}
                  data-testid={`gfc-risk-category-option-${index}`}
                  label={
                    <Card 
                      className={mergeClasses(styles.categoryOption, isSelected && styles.selectedOption)}
                      style={{ 
                        width: '100%',
                        cursor: 'pointer',
                        border: `1px solid ${isSelected ? colors.border : '#e1dfdd'}`,
                        backgroundColor: isSelected ? colors.background : '#ffffff',
                      }}
                      data-testid={`gfc-risk-category-card-${index}`}
                    >
                      <div className={styles.optionContent} data-testid={`gfc-risk-category-option-content-${index}`}>
                        <div className={styles.optionHeader} data-testid={`gfc-risk-category-option-header-${index}`}>
                          <div className={styles.categoryTitle} data-testid={`gfc-risk-category-title-${index}`}>
                            {getCategoryIcon(category.icon, category.key)}
                            <Text weight="semibold" size={400}>
                              {category.title}
                            </Text>
                          </div>
                          {getCategoryBadge(category.key)}
                        </div>
                        
                        <div className={styles.categoryDetails} data-testid={`gfc-risk-category-details-${index}`}>
                          <Text size={300}>
                            {category.description}
                          </Text>
                          
                          <div data-testid={`gfc-risk-category-characteristics-section-${index}`}>
                            <Text weight="medium" size={300} style={{ display: 'block', marginBottom: '6px' }}>
                              Characteristics:
                            </Text>
                            <ul className={styles.characteristicsList} data-testid={`gfc-risk-category-characteristics-list-${index}`}>
                              {category.characteristics.map((char, charIndex) => (
                                <li key={charIndex} data-testid={`gfc-risk-category-characteristic-${index}-${charIndex}`}>
                                  <Text size={200}>{char}</Text>
                                </li>
                              ))}
                            </ul>
                          </div>
                          
                          <div data-testid={`gfc-risk-category-use-case-section-${index}`}>
                            <Text weight="medium" size={300}>Use Case: </Text>
                            <Text size={300} style={{ fontStyle: 'italic', color: '#605e5c' }}>
                              {category.useCase}
                            </Text>
                          </div>
                        </div>
                      </div>
                    </Card>
                  }
                />
                
                {isSelected && getComplianceInfo(category.key)}
              </div>
            );
          })}
        </RadioGroup>

        {/* Risk Assessment Summary */}
        {value && (
          <Card style={{ marginTop: '16px', backgroundColor: '#f0f8ff', border: '1px solid #106ebe' }} data-testid="gfc-risk-category-summary">
            <div style={{ padding: '16px' }} data-testid="gfc-risk-category-summary-content">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }} data-testid="gfc-risk-category-summary-header">
                <Info24Regular style={{ color: '#106ebe' }} />
                <Text weight="semibold" size={400}>Risk Assessment Summary</Text>
              </div>
              
              <div style={{ display: 'grid', gap: '8px' }} data-testid="gfc-risk-category-summary-grid">
                <div style={{ display: 'flex', justifyContent: 'space-between' }} data-testid="gfc-risk-category-summary-selected-row">
                  <Text size={300}>Selected Category:</Text>
                  <Text size={300} weight="medium">{value} Risk</Text>
                </div>
                
                {complianceRegion && (
                  <div style={{ display: 'flex', justifyContent: 'space-between' }} data-testid="gfc-risk-category-summary-region-row">
                    <Text size={300}>Compliance Region:</Text>
                    <Text size={300} weight="medium">{complianceRequirements[complianceRegion]?.name}</Text>
                  </div>
                )}
                
                <div style={{ display: 'flex', justifyContent: 'space-between' }} data-testid="gfc-risk-category-summary-monitoring-row">
                  <Text size={300}>Monitoring Level:</Text>
                  <Text size={300} weight="medium">
                    {riskCategories.find(c => c.key === value)?.monitoringLevel.split(' ')[0]}
                  </Text>
                </div>
                
                <div style={{ display: 'flex', justifyContent: 'space-between' }} data-testid="gfc-risk-category-summary-audit-row">
                  <Text size={300}>Audit Frequency:</Text>
                  <Text size={300} weight="medium">
                    {riskCategories.find(c => c.key === value)?.auditRequirement.split(' ')[0]}
                  </Text>
                </div>
              </div>
            </div>
          </Card>
        )}
      </div>
    </Field>
  );
};